import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  getUser,
  login as loginRequest,
  logout as logoutRequest,
  onSessionChange,
  type LoginInput,
  type SessionUser,
} from './api.js';

/**
 * The React face of `api.ts`'s module-level session.
 *
 * ── Why the session state lives in a MODULE and this is only a view of it ──
 * `api.ts` needs the access token in a plain closure: the fetch wrapper is called from
 * TanStack Query's cache, from the router's loaders and from the softphone's own effects,
 * none of which are inside a React render and none of which can read a context. Putting the
 * token in state and the rest in a module would give two sources of truth for one fact.
 *
 * So the module owns it, and this subscribes. `onSessionChange` fires on login, on logout and
 * on a refresh that failed — which is what makes a session that dies in a background query
 * redirect the whole shell rather than leaving a signed-out user on a page full of stale data.
 */

interface SessionContextValue {
  user: SessionUser | undefined;
  /** True until `bootstrapSession()` has answered. The router must not decide before then. */
  loading: boolean;
  login(input: LoginInput): Promise<void>;
  logout(everywhere?: boolean): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({
  children,
  initialUser,
}: {
  children: ReactNode;
  /**
   * The answer from `bootstrapSession()`, resolved BEFORE this tree renders.
   *
   * Passing it in rather than fetching it in an effect is what stops the login page from
   * flashing on every reload for a user who is signed in: an effect cannot run before the
   * first paint, so the router would already have decided there was no session and redirected.
   */
  initialUser: SessionUser | undefined;
}): ReactNode {
  const [user, setUser] = useState<SessionUser | undefined>(initialUser);

  useEffect(() => {
    // Re-read on mount as well as subscribing: a login that completed between the bootstrap
    // and this effect would otherwise be missed entirely.
    setUser(getUser());
    return onSessionChange(setUser);
  }, []);

  const login = useCallback(async (input: LoginInput): Promise<void> => {
    await loginRequest(input);
    // No setUser here — `onSessionChange` above already fired. Setting it twice would be
    // harmless and would also make this the second place the rule lives.
  }, []);

  const logout = useCallback(async (everywhere = false): Promise<void> => {
    await logoutRequest(everywhere);
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ user, loading: false, login, logout }),
    [user, login, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside <SessionProvider>');
  return context;
}

/**
 * The signed-in user, for a component that is only ever rendered behind the auth guard.
 *
 * Throwing rather than returning `undefined` is deliberate: every consumer would otherwise
 * write `user?.email` and render a blank where a name should be, which looks like a data
 * problem. Reaching here with no user means the route was registered outside the guard, and
 * that is a wiring bug worth failing loudly on.
 */
export function useAuthenticatedUser(): SessionUser {
  const { user } = useSession();
  if (!user) throw new Error('this component is rendered outside the authenticated layout');
  return user;
}
